<template>
    <div class="flex flex-col h-full">
        <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
            <template slot="title">
                <div class="flex flex-grow max-w-4xl">
                    <div class="flex flex-col truncate">
                        <div class="truncate">[#{{ item.friendly_id }}] {{ item.subject }}</div>
                        <small class="text-xs ml-2">Tasks</small>
                    </div>
                </div>
            </template>
        </page-header>
        <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
        <div class="flex xl:flex-row lg:flex-col md:flex-col sm:flex-col flex-grow">
            <div class="xl:w-3/4 lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6 bg-white border-r border-gray-400 bg-gray-200">
                <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12" v-if="!loading" v-show="activeTab === 'details'">
                    <div class="sm:col-span-6">
                        <table class="w-full mb-4 internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Task Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Assignee</td>
                                    <td>{{ item.assignee ? item.assignee.name : '' }}</td>
                                </tr>
                                <tr>
                                    <td class="w-32 font-black">Due Date</td>
                                    <td>{{ new Date(item.due_date) | moment("DD/MM/YYYY h:mm a") }}</td>
                                </tr>
                                <tr>
                                    <td>Type</td>
                                    <td v-if="item.type">
                                        {{ item.type.name }}
                                    </td>
                                    <td v-else>N/A</td>
                                </tr>
                                <tr>
                                    <td>Status</td>
                                    <td v-if="item.status">
                                        {{ item.status.name }}
                                    </td>
                                    <td v-else>N/A</td>
                                </tr>
                                <tr>
                                    <td>Priority</td>
                                    <td v-if="item.priority">
                                        {{ item.priority.name }}
                                    </td>
                                    <td v-else>N/A</td>
                                </tr>
                                <tr>
                                    <td>Project</td>
                                    <td v-if="item.project">
                                        {{ item.project.name }}
                                    </td>
                                    <td v-else>N/A</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-6 flex flex-col">
                        <h3 class="py-2 px-2 border-b-2 border-black">Task Information</h3>
                        <p class="text-xs font-thin py-2 px-2 bg-white flex-grow">{{ item.description }}</p>
                    </div>
                    <div class="sm:col-span-12" v-if="item.files && item.files.length > 0">
                        <table class="w-full mb-4 internal-table">
                            <thead>
                            <tr>
                                <th>Documents/Files</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(file, index) in item.files" :key="index">
                                <td style="font-weight: normal;"><a :href="S3URL + '/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a></td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <notes v-if="!loading"
                    v-show="activeTab === 'notes'"
                    :id="item.id"
                    :notes="item.notes"
                    model="Task" />
                <attachments v-if="!loading"
                    v-show="activeTab === 'attachments'"
                    :id="item.id"
                    :attachments="item.attachments"
                    :folder="'tasks'"
                    model="Task" />
                <activity v-if="!loading && item.status_logs"
                    v-show="activeTab === 'activity'"
                    :activity="item.status_logs" />
            </div>
            <div class="xl:w-1/4 lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6">
                <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12" v-if="!loading">
                    <div class="sm:col-span-12">
                        <table class="w-full internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Linked Record</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="w-full xl:w-24">Type</td>
                                    <td>{{ item.model ? item.model.model : '' }}</td>
                                </tr>
                                <tr>
                                    <td class="w-full xl:w-24">Name</td>
                                    <td v-if="item.model && item.model.path">
                                        <router-link :to="'/'+item.model.path+'/'+item.model.id+'/view'" class="text-default-500">
                                            <span v-if="item.model.application">[{{ item.model.application.friendly_id_display }}] {{ item.model.application.member.first + ' ' + item.model.application.member.last }}</span>
                                            <span v-if="item.model.member">{{ item.model.member }}</span>
                                            <span v-if="item.model.supplier">{{ item.model.supplier }}</span>
                                        </router-link>
                                    </td>
                                    <td v-else>{{ item.model ? item.model.name : '' }}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-12">
                        <table class="w-full mb-6 internal-table">
                            <thead>
                            <tr>
                                <th colspan="2">Meta</th>
                            </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Last Updated</td>
                                    <td>{{ new Date(item.meta.updated_at) | moment("DD/MM/YYYY h:mm a") }}</td>
                                </tr>
                                <tr>
                                    <td>Created At</td>
                                    <td>{{ new Date(item.meta.created_at) | moment("DD/MM/YYYY h:mm a") }}</td>
                                </tr>
                                <tr>
                                    <td>Created By</td>
                                    <td>{{ item.meta.created_by ? item.meta.created_by.full_name : ''}}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Tabs from "@/mixins/Tabs";
    import axios from "axios";
    import Notes from "@/components/Notes";
    import Activity from "@/components/Activity";
    import Attachments from "@/components/Attachments";

    export default {
        name: "TaskView",
        props: {
            id: { required: true },
            return: { required: false, default: '/tasks' },
            page: { required: false, default: 'tasks' }
        },
        components: {
            Notes,
            Activity,
            Attachments
        },
        mixins: [
            Tabs
        ],
        data: function () {
            return {
                tabs: [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Notes',
                        slug: 'notes'
                    },
                    // {
                    //     name: 'Attachments',
                    //     slug: 'attachments'
                    // },
                    {
                        name: 'Activity',
                        slug: 'activity'
                    },
                ],
                startTab: null,
                loading: true,
                item: {},
                pageActions: {},
                inlineSaved: {
                    status_id: false,
                    type_id: false,
                    priority_id: false
                },
                inlineOptions: {
                    statuses: [],
                    types: [],
                    priorities: []
                },
                S3URL: process.env.VUE_APP_S3_URL,
            }
        },
        methods: {
            getData: function(){
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/tasks/'+this.id, {
                    params: {
                        with: 'notes,attachments'
                    }
                })
                .then(response => {
                    this.item = response.data;

                    this.pageActions = {
                        buttons: [
                            {
                                label: 'Edit',
                                icon: 'edit',
                                type: 'router-link',
                                link: '/tasks/'+ this.id + '/edit',
                                iconClass: 'text-gray-700',
                                display: !!(this.$store.getters.permissions.indexOf('tasks.edit') !== -1 || this.$store.getters.nimdarepus)
                            },
                            {
                                label: 'Close',
                                icon: 'times',
                                type: 'router-link',
                                iconClass: 'text-red-500',
                                link: this.return ? this.return : '/tasks',
                            },
                        ],
                        additional: [
                            {
                                label: 'Delete',
                                icon: 'trash-alt',
                                type: 'method',
                                method: 'deleteModal',
                                iconClass: 'text-red-500',
                                display: !!(this.$store.getters.permissions.indexOf('tasks.destroy') !== -1 || this.$store.getters.nimdarepus)
                            }
                        ]
                    };

                    this.$Progress.finish();
                    this.loading = false;
                })
                .catch(() => {
                    this.$Progress.finish();
                    this.loading = false;
                });
            },
            saveInput: function (inputName, inputValue){

                // reset the saved flag on the inlineInput watcher
                this.inlineSaved[inputName] = false;

                this.$store.commit('TOGGLE_BACKDROP', {open: true});

                // persit the changes to the api
                axios({
                    method: 'PUT',
                    url: process.env.VUE_APP_API_URL + '/tasks/' + this.id,
                    data: { [inputName]: inputValue }
                }).then(response => {
                    if (response.data.success) {

                        // update the items activity logs
                        if(response.data.data && response.data.data.status_logs){
                            this.item.status_logs = response.data.data.status_logs;
                        }

                        // set the saved flag on the inlineInput watcher
                        this.inlineSaved[inputName] = true;

                        // saved notificsations
                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Success',
                            text: response.data.message,
                            duration: 5000
                        });

                    } else {

                        if (response.data.message) {
                            this.errorMessage = response.data.message;
                        }
                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    }
                }).catch(error => {
                    if (error.response.data.message) {
                        this.errorMessage = error.response.data.message;
                    } else {
                        this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                    }

                    this.$store.commit('TOGGLE_BACKDROP', {open: false});
                });


            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            }
        },
        beforeMount() {
            // Statuses
            if (this.$store.getters.taskStatuses.length === 0){
                this.$store.dispatch('GET_TASK_STATUSES').then(() => {
                    this.inlineOptions.statuses = this.$store.getters.taskStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.inlineOptions.statuses = this.$store.getters.taskStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            // Types
            if (this.$store.getters.taskTypes.length === 0){
                this.$store.dispatch('GET_TASK_TYPES').then(() => {
                    this.inlineOptions.types = this.$store.getters.taskTypes.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.inlineOptions.types = this.$store.getters.taskTypes.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            // Priorities
            if (this.$store.getters.taskPriorities.length === 0){
                this.$store.dispatch('GET_TASK_PRIORITIES').then(() => {
                    this.inlineOptions.priorities = this.$store.getters.taskPriorities.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.inlineOptions.priorities = this.$store.getters.taskPriorities.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

        },
        mounted() {
            this.getData();
        },
        computed: {
            initialValue: function(){
                let output = {};

                if (this.item.status){
                    output['status'] = this.item.status.id;
                } else {
                    output['status'] = null
                }

                if (this.item.type){
                    output['type'] = this.item.type.id;
                } else {
                    output['type'] = null
                }

                if (this.item.priority){
                    output['priority'] = this.item.priority.id;
                } else {
                    output['priority'] = null
                }

                return output;
            }
        }
    }
</script>

<style scoped>

</style>
